import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, Pencil } from 'lucide-react';
import { bpmnId, mapMetrics, parseBpmnId } from '@caliber-2/discovery-core/maps';
import { StatusChip } from '@/components/ui/status-chip';
import { WorkspaceButton, WorkspaceLayout } from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import {
  referenceChatKey,
  referenceMapApi,
  useReferenceChatQuery,
  type FrameworkProcess,
  type ReferenceProposal,
} from '@/lib/api/discovery-frameworks';
import { saveBlob } from '@/lib/utils/download';
import { MapCanvas } from '@/components/discovery/maps/map-canvas';
import { MapChatPanel, activeChatChangeSet } from '@/components/discovery/maps/map-chat-panel';
import {
  STEP_TYPE_LABEL,
  bpmnFor,
  chatSuggestions,
  isEmptyModel,
  mergeMarkers,
  modelFromReference,
  previewMarkers,
  proposedMap,
  refusalText,
  type ChangeOperation,
  type MapModel,
} from '@/components/discovery/maps/map-logic';
import {
  EditBanner,
  MapLegend,
  MapMetricsRow,
  MapProvenance,
  MapStatusLine,
  PendingBanner,
  type MapStatus,
} from '@/components/discovery/maps/map-parts';
import { ReferenceStepPanel } from '@/components/discovery/maps/step-panel';
import { useMapEditor } from '@/components/discovery/maps/use-map-editor';
import type { EditorCtx } from '../editor';

/**
 * A framework's reference process in Firm settings (T-0189, E12-F01 §2,
 * ADR-017 §3): the skeleton AI drafts an engagement's map from. The same
 * canvas, layout, checks, chat and BPMN download as an engagement's map
 * (prototype `refProcessView()`). It cites nothing.
 *
 * Changes go into the framework draft: a chat proposal or the canvas edits,
 * checked by code, shown on the map, and written to the draft only on
 * Apply. The chat bills the firm's AI budget (Decision 54.2). Outside a
 * draft the map is read-only and Edit opens the draft.
 */
export interface ReferenceMapViewProps {
  ctx: EditorCtx;
  frameworkId: string;
  process: FrameworkProcess;
  areaLabel: string;
  /** The published version shown, or the draft's number when editing. */
  versionLabel: string;
  canStartDraft: boolean;
  onStartDraft: () => void;
  onBack: () => void;
}

type Dock = 'chat' | 'hidden';

const CONTEXT = { origin: 'canvas' as const, user_id: null };

function messageOf(err: unknown): string {
  if (err instanceof ApiError) {
    const reason = err.details?.['reason'];
    if (reason === 'map.invalid_change_set') return refusalText(err.message);
    return err.message;
  }
  return err instanceof Error ? err.message : 'That did not work.';
}

function slug(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'process'
  );
}

export function ReferenceMapView({
  ctx,
  frameworkId,
  process,
  areaLabel,
  versionLabel,
  canStartDraft,
  onStartDraft,
  onBack,
}: ReferenceMapViewProps) {
  const qc = useQueryClient();
  const draft = ctx.editing;
  const base = React.useMemo(
    () => modelFromReference(process.reference_map),
    [process.reference_map],
  );
  const chatQ = useReferenceChatQuery(frameworkId, process.id, draft);
  const [dock, setDock] = React.useState<Dock>('chat');
  const [selected, setSelected] = React.useState<string | null>(null);
  const [local, setLocal] = React.useState<{ id: string; operations: ChangeOperation[] } | null>(
    null,
  );
  const [status, setStatus] = React.useState<MapStatus | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const note = React.useCallback(
    (text: string, tone: 'default' | 'critical') => setStatus({ text, tone }),
    [],
  );
  const editor = useMapEditor(base, CONTEXT, note);

  // Leaving the draft (published, discarded) ends edits and proposals.
  React.useEffect(() => {
    if (!draft) {
      editor.stop();
      setLocal(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const refreshChat = () =>
    qc.invalidateQueries({ queryKey: referenceChatKey(frameworkId, process.id) });
  const activeChat = draft ? activeChatChangeSet(chatQ.data) : null;
  const proposal = editor.editing
    ? null
    : local
      ? { id: local.id, operations: local.operations as unknown as Array<Record<string, unknown>> }
      : activeChat
        ? { id: activeChat.id, operations: activeChat.operations }
        : null;
  const proposed = React.useMemo(
    () =>
      proposal
        ? proposedMap(base, proposal.operations, {
            origin: local ? 'canvas' : 'chat',
            user_id: null,
          })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, proposal?.id],
  );
  const drawn: MapModel = editor.editing ? editor.model : (proposed?.model ?? base);
  const xml = React.useMemo(
    () => bpmnFor(drawn, { process: process.name, participant: 'Client' }),
    [drawn, process.name],
  );
  const markers = React.useMemo(
    () =>
      mergeMarkers(
        proposed ? previewMarkers(proposed.preview) : {},
        selected ? { [bpmnId('step', selected)]: ['mk-sel'] } : {},
      ),
    [proposed, selected],
  );

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      setStatus({ text: messageOf(err), tone: 'critical' });
    } finally {
      setBusy(false);
    }
  };

  const apply = (id: string) =>
    run(async () => {
      const box: { err: unknown } = { err: null };
      const done = await ctx.write(
        (fwId, rev) => referenceMapApi.apply(fwId, rev, process.id, id),
        { what: process.name, onError: (err) => (box.err = err) },
      );
      if (box.err) throw box.err;
      if (!done) return; // The draft was reloaded; the screen says why.
      setLocal(null);
      setSelected(null);
      setStatus({
        text: 'Applied to the draft. It is published with the framework. The change is in the audit trail.',
        tone: 'default',
      });
      await refreshChat();
    });

  const discard = (id: string) =>
    run(async () => {
      await referenceMapApi.discard(frameworkId, process.id, id);
      setLocal(null);
      setStatus({ text: 'Discarded. The reference process is unchanged.', tone: 'default' });
      await refreshChat();
    });

  const doneEditing = () => {
    if (editor.batch.length === 0) {
      editor.stop();
      setStatus(null);
      return;
    }
    const issue = editor.issues[0];
    if (issue) {
      setStatus({
        text: `Can't save yet: ${issue.message} Connect it or remove it, then choose Done editing again.`,
        tone: 'critical',
      });
      return;
    }
    const batch = editor.batch;
    void run(async () => {
      const box: { err: unknown } = { err: null };
      const r = (await ctx.write(
        (fwId, rev) =>
          referenceMapApi.propose(
            fwId,
            rev,
            process.id,
            batch as unknown as Array<Record<string, unknown>>,
          ),
        { what: process.name, onError: (err) => (box.err = err) },
      )) as ReferenceProposal | null;
      if (box.err) throw box.err;
      if (!r) return;
      if (!r.valid) {
        setStatus({ text: refusalText(r.invalid_reason), tone: 'critical' });
        return;
      }
      editor.stop();
      setStatus(null);
      setLocal({ id: r.change_set_id, operations: batch });
    });
  };

  const sendChat = async (text: string): Promise<boolean> => {
    const rev = ctx.content.version?.draft_revision;
    if (rev == null) return false;
    setSending(true);
    try {
      await referenceMapApi.send(frameworkId, rev, process.id, text);
      setSelected(null);
      await refreshChat();
      return true;
    } catch (err) {
      setStatus({ text: messageOf(err), tone: 'critical' });
      return false;
    } finally {
      setSending(false);
    }
  };

  const download = () => {
    const name = `${slug(process.name)}-reference.bpmn`;
    saveBlob(
      name,
      new Blob([bpmnFor(base, { process: process.name, participant: 'Client' })], {
        type: 'application/xml',
      }),
    );
    setStatus({
      text: `Downloaded ${name}, a BPMN 2.0 file. It opens in Camunda Modeler, Signavio and other BPMN tools.`,
      tone: 'default',
    });
  };

  const onSelect = (elementId: string) => {
    if (editor.editing) return;
    const id = parseBpmnId(elementId);
    if (id?.kind !== 'step') return;
    const step = drawn.steps.find((s) => s.key === id.key);
    if (!step || step.type === 'start_event' || step.type === 'end_event') return;
    setSelected(step.key);
  };

  const empty = isEmptyModel(base) && !editor.editing && !proposed;
  const selectedStep = selected ? drawn.steps.find((s) => s.key === selected) : undefined;
  const chatOff = !draft
    ? 'Select Edit to change this reference process in a draft.'
    : editor.editing
      ? 'Finish editing first.'
      : local
        ? 'Apply or discard the change shown first.'
        : null;

  const panel = selectedStep ? (
    <ReferenceStepPanel
      key={selectedStep.key}
      name={selectedStep.name}
      path={`${drawn.lanes.find((l) => l.key === selectedStep.lane)?.name ?? ''} · ${STEP_TYPE_LABEL[selectedStep.type]}`}
      provenance={
        selectedStep.origin === 'chat'
          ? 'Added in chat in the draft.'
          : selectedStep.origin === 'canvas' || selectedStep.origin === 'manual'
            ? 'Added by hand.'
            : null
      }
      editing={draft}
      onClose={() => setSelected(null)}
    />
  ) : dock === 'chat' ? (
    <MapChatPanel
      versionLabel={versionLabel}
      thread={draft ? chatQ.data : { thread_id: null, messages: [] }}
      error={draft ? chatQ.error : null}
      intro="Tell me what to change, in your own words. I'll show the change on the map first; nothing changes until you apply it. Changes go into the framework draft and are published with it."
      suggestions={chatSuggestions(base, 'reference')}
      disabledReason={chatOff}
      sending={sending}
      activeId={activeChat?.id ?? null}
      busy={busy}
      footNote="This chat has no engagement, so it draws on the firm's AI budget (work class: chat)."
      onSend={sendChat}
      onApply={(cs) => void apply(cs.id)}
      onDiscard={(cs) => void discard(cs.id)}
      onHide={() => setDock('hidden')}
    />
  ) : null;

  const toolbar = (
    <>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1 text-meta font-medium text-ink-700 underline underline-offset-[3px] hover:text-ink-900"
      >
        <ArrowLeft className="h-3 w-3" aria-hidden="true" />
        Areas
      </button>
      <span className="text-meta text-ink-500">/ {areaLabel} /</span>
      <span className="text-body-sm font-semibold text-ink-900">{process.name}</span>
      <span className="text-meta text-ink-500">reference process</span>
      <span className="flex-1" />
      {dock === 'hidden' && !selectedStep ? (
        <WorkspaceButton onClick={() => setDock('chat')}>Chat</WorkspaceButton>
      ) : null}
      {draft ? (
        <WorkspaceButton
          disabled={busy || (isEmptyModel(base) && !editor.editing)}
          title={isEmptyModel(base) ? 'Ask the chat to draw it first' : undefined}
          onClick={
            editor.editing
              ? doneEditing
              : () => {
                  setSelected(null);
                  setStatus(null);
                  if (local) void discard(local.id);
                  editor.start();
                }
          }
        >
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          {editor.editing ? 'Done editing' : 'Edit map'}
        </WorkspaceButton>
      ) : canStartDraft ? (
        <WorkspaceButton onClick={onStartDraft}>
          <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          Edit
        </WorkspaceButton>
      ) : null}
      <WorkspaceButton
        disabled={isEmptyModel(base) || editor.editing}
        title="Download as BPMN 2.0"
        onClick={download}
      >
        <Download className="h-3.5 w-3.5" aria-hidden="true" />
        BPMN
      </WorkspaceButton>
    </>
  );

  return (
    <WorkspaceLayout
      toolbar={toolbar}
      panel={panel}
      panelMode="docked"
      footer={
        empty ? null : (
          <MapMetricsRow
            metrics={mapMetrics(drawn)}
            kind="reference"
            proposed={Boolean(proposed) || editor.batch.length > 0}
          />
        )
      }
    >
      <MapProvenance>
        {draft ? (
          <StatusChip tone="amber">In {versionLabel}</StatusChip>
        ) : (
          <StatusChip tone="neutral">Published in {versionLabel}</StatusChip>
        )}
        <span>
          A reference process cites nothing. Engagements on a later version get it when their lead
          moves them.
        </span>
      </MapProvenance>
      {editor.editing ? (
        <EditBanner
          reference
          onDiscard={() => {
            editor.stop();
            setStatus({ text: 'Edits discarded. The map is as it was.', tone: 'default' });
          }}
        />
      ) : null}
      {local && !editor.editing ? (
        <PendingBanner
          title="Your edits, as one change."
          lines={[]}
          busy={busy}
          onApply={() => void apply(local.id)}
          onDiscard={() => void discard(local.id)}
        />
      ) : null}
      <MapLegend
        kind="reference"
        previewing={Boolean(proposed)}
        hint={
          editor.editing
            ? 'Drag, add, connect or delete; code lays the map out again.'
            : 'Click a step to see it. Ask the chat to change it, or edit it by hand.'
        }
      />
      <MapStatusLine status={status} onDismiss={() => setStatus(null)} />
      {empty ? (
        <div className="m-4 flex flex-1 flex-col items-center justify-center gap-1.5 rounded-card border border-dashed border-ink-200 p-8 text-center">
          <strong className="text-body font-semibold text-ink-900">No reference process yet</strong>
          <p className="max-w-md text-body-sm text-ink-500">
            {draft
              ? 'Ask the chat to draw it: the lanes (the roles), the steps in order, and any decisions. Code lays it out.'
              : 'Select Edit to draw one in a draft of this framework.'}
          </p>
        </div>
      ) : (
        <MapCanvas
          xml={xml}
          markers={markers}
          editable={editor.editing}
          redraw={editor.redraw}
          label={`${process.name}, reference process`}
          onSelect={onSelect}
          onCanvasChange={editor.onCanvasChange}
        />
      )}
    </WorkspaceLayout>
  );
}
